import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { JoBadge, fmtDate, fmtN } from './JoBits';
import { JwEwbCell, JwTransferPrintBtn } from '@/components/jobWork/JwTransfer';

const pickedPcs = (s) => (['CANCELLED', 'REJECTED'].includes(s.status) ? null : (s.bundles || []).filter((b) => b.picked).reduce((a, b) => a + (b.pcs || 0), 0));
const slipPcs = (s) => (s.bundles || []).reduce((a, b) => a + (b.pcs || 0), 0);
const cText = (cols) => (cols || []).map((c) => `${c.color_name} ${c.requested_pcs}`).join(', ');
export const lineText = (l) => {
  const parts = [cText(l.colors), l.parent_colors?.length ? `parent ${l.parent_sort_no}: ${cText(l.parent_colors)}` : ''].filter(Boolean);
  return `${l.sort_no} × ${l.requested_pcs}${parts.length ? ` (${parts.join('; ')})` : ''}`;
};
const articles = (s) => (s.lines || []).map(lineText).join(', ');

/** Every pick slip of a job order: base articles, pieces, picker, status. */
export const JoSlipsTable = ({ slips, onOpen, testPrefix = 'jo-slip' }) => (
  <div className="overflow-x-auto">
    <Table data-testid={`${testPrefix}-table`}>
      <TableHeader><TableRow>{['Pick slip', 'Base articles (pcs asked)', 'Bundles', 'Pieces', 'Picked', 'Picker', 'Transfer', 'Status'].map((h) => <TableHead key={h} className="whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {!slips.length && <TableRow><TableCell colSpan={8} className="text-center py-6 text-gray-500" data-testid={`${testPrefix}-empty`}>No pick slips yet</TableCell></TableRow>}
        {slips.map((s) => (
          <TableRow key={s.slip_id} onClick={onOpen ? () => onOpen(s) : undefined} className={onOpen ? 'cursor-pointer hover:bg-[#F5EFE3]' : ''} data-testid={`${testPrefix}-row-${s.slip_id}`}>
            <TableCell><div className="font-mono text-sm">{s.slip_id}</div><div className="text-[11px] text-gray-500">{fmtDate(s.created_at)} · {s.created_by_name}</div></TableCell>
            <TableCell className="font-mono text-xs max-w-[220px] whitespace-normal">{articles(s)}</TableCell>
            <TableCell className="tabular-nums">{(s.bundles || []).length}</TableCell>
            <TableCell className="tabular-nums" data-testid={`${testPrefix}-pcs-${s.slip_id}`}>{fmtN(slipPcs(s))}</TableCell>
            <TableCell className="tabular-nums" data-testid={`${testPrefix}-picked-${s.slip_id}`}>{pickedPcs(s) === null ? <span className="text-gray-400" title="Bundles were put back in stock">—</span> : fmtN(pickedPcs(s))}</TableCell>
            <TableCell className="text-sm">{(s.pickers || []).join(', ') || '—'}</TableCell>
            <TableCell className="font-mono text-xs">{s.transfer_id || '—'}</TableCell>
            <TableCell><JoBadge status={s.status} testId={`${testPrefix}-status-${s.slip_id}`} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

/** Every transfer / consignment of a job order with the assigned jobber, pieces sent / received / with the jobber, and its e-way bill. */
export const JoConsignmentsTable = ({ rows, ewb = {} }) => (
  <div className="overflow-x-auto">
    <Table data-testid="jo-consignments-table">
      <TableHeader><TableRow>{['Transfer', 'Pick slip', 'Jobber', 'Bundles', 'Sent', 'Received', 'With jobber', 'Stage', 'E-way bill', ''].map((h) => <TableHead key={h} className="whitespace-nowrap">{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {!rows.length && <TableRow><TableCell colSpan={10} className="text-center py-6 text-gray-500" data-testid="jo-consignments-empty">No transfers yet</TableCell></TableRow>}
        {rows.map((r) => (
          <TableRow key={r.transfer_id} data-testid={`jo-it-row-${r.transfer_id}`}>
            <TableCell><div className="font-mono text-sm">{r.transfer_id}</div><div className="text-[11px] text-gray-500">{fmtDate(r.created_at)}</div></TableCell>
            <TableCell className="font-mono text-xs">{r.slip_id}</TableCell>
            <TableCell className="text-sm" data-testid={`jo-it-jobber-${r.transfer_id}`}>{r.jobber_name || <span className="text-amber-700">Not assigned yet</span>}</TableCell>
            <TableCell className="tabular-nums">{r.bundles}</TableCell>
            <TableCell className="tabular-nums">{fmtN(r.sent_pcs)}</TableCell>
            <TableCell className="tabular-nums text-emerald-700" data-testid={`jo-it-received-${r.transfer_id}`}>{fmtN(r.received_pcs)}</TableCell>
            <TableCell className="tabular-nums text-rose-700">{fmtN(r.with_jobber_pcs)}</TableCell>
            <TableCell className="text-sm" data-testid={`jo-it-stage-${r.transfer_id}`}>{r.stage}</TableCell>
            <TableCell><JwEwbCell s={ewb[r.transfer_id]} testId={`jo-it-ewb-${r.transfer_id}`} /></TableCell>
            <TableCell>{!r.rejected && <JwTransferPrintBtn kind="processing" refNo={r.transfer_id} s={ewb[r.transfer_id]} label="Transfer" testId={`jo-it-transfer-print-${r.transfer_id}`} />}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);
